import { useRef } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { IconType } from 'react-icons';

import {
  animateTealIconHighlight,
  createStepHighlightCycle,
  killStepHighlightTweens,
  SBR_STEP_HIGHLIGHT_DURATION,
  SBR_TEAL_ICON_ON_LIGHT,
} from '../../lib/gsapHighlight';
import { nodeListToArray } from '../../lib/dom';
import SbrIcon from './SbrIcon';

export type AdvantageTileItem = {
  id?: string;
  title: string;
  body: string;
  Icon: IconType;
  stat?: string;
};

type AdvantageTilesGridProps = {
  items: AdvantageTileItem[];
  className?: string;
  colSm?: number;
  colMd?: number;
  colLg?: number;
  colXl?: number;
  /** Cycle teal highlight across tile icons after scroll reveal (default on). */
  cycleIcons?: boolean;
};

/** Card tiles matching homepage SBR2TH Advantage — scroll stagger + icon cycle. */
export default function AdvantageTilesGrid({
  items,
  className = '',
  colSm,
  colMd = 6,
  colLg,
  colXl = 3,
  cycleIcons = true,
}: AdvantageTilesGridProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const itemsSignature = items.map((item) => item.id ?? `${item.stat}-${item.title}`).join('|');

  useGSAP(
    () => {
      if (!rootRef.current) return;

      const cards = rootRef.current.querySelectorAll<HTMLElement>('.home-advantage-card');
      const icons = rootRef.current.querySelectorAll<HTMLElement>('.home-advantage-card__icon');
      if (!cards.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let cycleTween: gsap.core.Timeline | null = null;
      let scrollTrigger: ScrollTrigger | undefined;

      const highlightTile = (index: number) => {
        killStepHighlightTweens(cards, icons);

        cards.forEach((card, j) => {
          const icon = icons[j];
          const isActive = j === index;

          if (icon) {
            animateTealIconHighlight(icon, isActive, SBR_TEAL_ICON_ON_LIGHT, 1.1);
          }

          if (isActive) {
            gsap.to(card, {
              y: -6,
              boxShadow: '0 14px 32px rgba(10, 15, 31, 0.14)',
              duration: SBR_STEP_HIGHLIGHT_DURATION,
              ease: 'power2.out',
              overwrite: 'auto',
            });
          } else {
            gsap.set(card, {
              y: 0,
              boxShadow: '0 0.125rem 0.25rem rgba(10, 15, 31, 0.075)',
            });
          }
        });
      };

      const startIconCycle = () => {
        if (!cycleIcons || !icons.length) return;

        cycleTween?.kill();
        highlightTile(0);
        if (reducedMotion) return;

        cycleTween = createStepHighlightCycle(items.length, highlightTile);
      };

      if (reducedMotion) {
        gsap.set(cards, { opacity: 1, y: 0 });
        startIconCycle();
        return () => {
          cycleTween?.kill();
        };
      }

      gsap.set(cards, { opacity: 0, y: 32 });

      let revealStarted = false;
      const reveal = gsap.timeline({
        paused: true,
        onComplete: startIconCycle,
      });

      cards.forEach((card, i) => {
        const offset = i * 0.14;
        reveal.to(card, { opacity: 1, y: 0, duration: 0.65, ease: 'power2.out' }, offset);

        const icon = icons[i];
        if (icon) {
          reveal.fromTo(
            icon,
            { scale: 0.75, opacity: 0.6 },
            { scale: 1, opacity: 1, duration: 0.55, ease: 'back.out(1.5)' },
            offset + 0.06,
          );
        }
      });

      const playReveal = () => {
        if (revealStarted) return;
        revealStarted = true;
        reveal.play();
      };

      scrollTrigger = ScrollTrigger.create({
        trigger: rootRef.current,
        start: 'top 82%',
        onEnter: playReveal,
        onEnterBack: playReveal,
      });

      if (ScrollTrigger.isInViewport(rootRef.current)) {
        playReveal();
      }

      return () => {
        cycleTween?.kill();
        scrollTrigger?.kill();
        reveal.kill();
        if (rootRef.current) {
          const resetCards = rootRef.current.querySelectorAll<HTMLElement>('.home-advantage-card');
          const resetIcons = rootRef.current.querySelectorAll<HTMLElement>('.home-advantage-card__icon');
          gsap.set(nodeListToArray(resetCards, resetIcons), { clearProps: 'all' });
        }
      };
    },
    { scope: rootRef, dependencies: [itemsSignature, cycleIcons, items.length] },
  );

  return (
    <Row ref={rootRef} className={`g-4 justify-content-center advantage-tiles-grid ${className}`.trim()}>
      {items.map((item) => (
        <Col
          key={item.id ?? item.title}
          xs={12}
          {...(colSm != null ? { sm: colSm } : {})}
          md={colMd}
          {...(colLg != null ? { lg: colLg } : {})}
          xl={colXl}
          className="d-flex"
        >
          <Card className="h-100 w-100 home-advantage-card shadow-sm sbr-card-hover border-0">
            <Card.Body className="home-advantage-card__body">
              <div className="home-advantage-card__icon flex-shrink-0" aria-hidden>
                <SbrIcon icon={item.Icon} size={24} />
              </div>
              <div className="home-advantage-card__content">
                {item.stat ? <div className="home-advantage-card__stat">{item.stat}</div> : null}
                <Card.Title className="home-advantage-card__title">{item.title}</Card.Title>
                <Card.Text className="home-advantage-card__text">{item.body}</Card.Text>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
